/**
 * KeyRotationSetting — shows when the user's cryptographic identity
 * was last rotated, and offers a manual "Rotate now" button for
 * paranoid users who want to force a key rollover between the
 * automatic 7-day cycles.
 *
 * The automatic rotation already runs opportunistically on login
 * when 7 days have elapsed — this surface is purely informational
 * + an opt-in fast-path. Nothing here touches keys directly; all
 * crypto lives in `utils/keyRotation.js` + `AuthContext`.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { KeyRound, Loader2, RotateCw } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import {
  lastRotationAt,
  getRotationIntervalMs,
  getRotationIntervalDays,
} from '../utils/keyRotation';
import { RotationCadenceSparkline } from './RotationCadenceSparkline';

const _fmtRelative = (ts) => {
  if (!ts) return 'never';
  const diff = Date.now() - ts;
  if (diff < 0) return 'just now';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  const hrs = Math.floor(mins / 60);
  if (hrs < 1) return `${mins}m ago`;
  const days = Math.floor(hrs / 24);
  if (days < 1) return `${hrs}h ago`;
  return `${days}d ago`;
};

const _fmtExact = (ts) => {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString(); } catch { return String(ts); }
};

export const KeyRotationSetting = () => {
  const { rotateKeysNow, cryptoKeys } = useAuth();
  const [ts, setTs] = useState(lastRotationAt());
  const [busy, setBusy] = useState(false);

  // Re-read the timestamp when cryptoKeys changes — rotation
  // updates localStorage + cryptoKeys together, so the React
  // identity change here is our cue to refresh the display.
  useEffect(() => {
    setTs(lastRotationAt());
  }, [cryptoKeys?.publicKeyJwk]);

  const nextDue = ts ? ts + getRotationIntervalMs() : Date.now();
  const dueIn = nextDue - Date.now();
  const overdue = dueIn <= 0;
  const cadenceDays = getRotationIntervalDays();

  const onRotate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await rotateKeysNow();
      setTs(Date.now());
      toast.success('Keys rotated. Contacts will see a new fingerprint.');
    } catch (e) {
      toast.error(e?.message || 'Rotation failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="key-rotation-setting">
      <label className="text-sm text-slate-400 flex items-center gap-2">
        <KeyRound className="w-4 h-4" />
        Cryptographic key rotation
      </label>
      <div className="p-4 rounded-lg bg-secondary/30 space-y-3">
        <p className="text-xs text-slate-500">
          Your RSA + Kyber-768 keypairs rotate automatically every {cadenceDays} {cadenceDays === 1 ? 'day' : 'days'}.
          The Dilithium identity key stays stable so peers can verify the
          rotation is yours. Messages encrypted against older keypairs
          remain decryptable on this device (archive ring kept locally;
          never uploaded, wiped by panic button).
        </p>

        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="rounded-md bg-background/40 border border-border/60 p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">
              Last rotation
            </div>
            <div
              className="font-mono text-slate-200 mt-0.5"
              title={_fmtExact(ts)}
              data-testid="key-rotation-last"
            >
              {_fmtRelative(ts)}
            </div>
          </div>
          <div className="rounded-md bg-background/40 border border-border/60 p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">
              Next automatic
            </div>
            <div
              className={`font-mono mt-0.5 ${overdue ? 'text-amber-300' : 'text-slate-200'}`}
              data-testid="key-rotation-next"
            >
              {overdue
                ? 'on next login'
                : `in ${Math.ceil(dueIn / 86400000)}d`}
            </div>
          </div>
        </div>

        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={onRotate}
          disabled={busy || !cryptoKeys?.dilithiumSecret}
          className="text-xs"
          data-testid="key-rotation-manual-btn"
        >
          {busy ? (
            <>
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Rotating…
            </>
          ) : (
            <>
              <RotateCw className="w-3.5 h-3.5 mr-1" /> Rotate now
            </>
          )}
        </Button>

        {/* Visible rhythm — the `key` prop forces a refetch when we
            bump `ts` after a successful rotation so the new dot
            appears without a page reload. */}
        <RotationCadenceSparkline
          key={ts || 'none'}
          className="text-emerald-400"
          label="Your rotation rhythm"
        />
      </div>
    </div>
  );
};

export default KeyRotationSetting;
